/**
 * Tipos internos da tela das manutenções.
 *
 * Papel: aqui ficam os formatos que o react-hook-form guarda para a corretiva e
 * para a preventiva, e os dois valores em branco de onde a criação parte.
 *
 * Os dois são derivados dos schemas do contrato com `z.input`, e não escritos à
 * mão, porque é a entrada daqueles schemas que o formulário precisa produzir.
 */
import { type novaCorretivaSchema, type novaPreventivaSchema } from '@concierge/shared';
import { type z } from 'zod';

/** A corretiva como o formulário a guarda, antes de o schema normalizar. */
export type CorretivaNoFormulario = z.input<typeof novaCorretivaSchema>;

/** A preventiva como o formulário a guarda. */
export type PreventivaNoFormulario = z.input<typeof novaPreventivaSchema>;

/**
 * Uma corretiva em branco, como o formulário de criação começa.
 *
 * Tudo vazio e o status "Em Orçamento / Em Tratativa", o primeiro do `select`
 * da origem (l. 1063). A última atualização também nasce vazia: ela não é
 * preenchida com hoje. `residenciaId` é preenchido por quem monta o formulário,
 * com a casa que a tela mostra.
 */
export const CORRETIVA_EM_BRANCO: CorretivaNoFormulario = {
  residenciaId: '',
  area: '',
  descricao: '',
  status: 'em_orcamento',
  responsavel: '',
  atualizacao: '',
  custo: '',
  observacao: '',
};

/**
 * Uma preventiva em branco, como o formulário de criação começa.
 *
 * Tudo vazio e o status "Em dia", o primeiro do `select` da origem (l. 1108).
 */
export const PREVENTIVA_EM_BRANCO: PreventivaNoFormulario = {
  residenciaId: '',
  numero: '',
  elemento: '',
  atividade: '',
  status: 'em_dia',
  periodicidade: '',
  empresa: '',
  responsavel: '',
  ultimaVisita: '',
  proximaVisita: '',
  custo: '',
  observacao: '',
};

/**
 * O estado de uma lista vinda da API, como a tabela o mostra.
 *
 * `carregando` e `falhou` só existem enquanto não há dado nenhum para mostrar;
 * com dado em cache, a tabela mostra o dado mesmo que a releitura tenha falhado.
 */
export type SituacaoDaLista = 'carregando' | 'falhou' | 'pronta';
